/**
 * Query keys do domínio PDV (catálogo, estoque, comandas, relatórios).
 *
 * Review PR#418 (IR-RQ-001): os hooks do PDV nasceram com keys inline
 * (`['club_products', ...]`, `['pdv_reports', ...]`) que NENHUMA mutação
 * invalidava por prefixo — estorno/fechamento não atualizavam a página de
 * relatórios dentro do staleTime (mesma classe de bug do header de
 * financial.ts / P3-2). Mutações do PDV invalidam `qk.pdv.all` (prefix-match
 * cobre tudo abaixo) + `qk.financial.all` quando mexem em receita.
 */
import { tuple } from "./core";

const root = ["pdv"] as const;

export const pdv = {
  /** Prefixo raiz — mutações do PDV invalidam isto (cobre tudo abaixo). */
  all: root,
  /** Prefixo do catálogo — mutações de produto invalidam só isto. */
  productsAll: tuple(root, "products"),
  /** Catálogo de produtos (`club_products`). */
  products: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "products", clubId, viewScope),
  /** Prefixo das comandas abertas — mutações de item invalidam só isto. */
  openSalesAll: tuple(root, "openSales"),
  /** Comandas ABERTAS (`club_sales` status=open, com itens). */
  openSales: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "openSales", clubId, viewScope),
  /** Histórico paginado de comandas fechadas/canceladas. */
  salesHistory: (clubId: string | null | undefined, viewScope: unknown, page: number) =>
    tuple(root, "salesHistory", clubId, viewScope, page),
  /** Resumo do dia (vendas fechadas hoje, BRT). */
  todaySummary: (clubId: string | null | undefined, viewScope: unknown, day: string) =>
    tuple(root, "todaySummary", clubId, viewScope, day),
  /** Picker leve de alunos do PDV. */
  studentOptions: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "studentOptions", clubId, viewScope),
  /** Relatórios de vendas por período. */
  report: (
    clubId: string | null | undefined,
    viewScope: unknown,
    startDay: string,
    endDay: string,
  ) => tuple(root, "report", clubId, viewScope, startDay, endDay),
  /** Fechamento de caixa de um dia (vendas fechadas + estornos, BRT). */
  dailyClosing: (clubId: string | null | undefined, viewScope: unknown, day: string) =>
    tuple(root, "dailyClosing", clubId, viewScope, day),
  /** Venda única com itens, para o recibo não-fiscal. */
  receipt: (saleId: string | null) => tuple(root, "receipt", saleId),
  /** Situação de crédito (a prazo) de um devedor — mesma RPC que o trigger usa. */
  creditStatus: (
    clubId: string | null | undefined,
    studentId: string | null | undefined,
    cpf: string | null | undefined,
  ) => tuple(root, "creditStatus", clubId, studentId, cpf),
  /** Recebimentos de uma comanda a prazo. */
  settlements: (saleId: string | null | undefined) => tuple(root, "settlements", saleId),
  /** Política de venda a prazo do clube (`club_pdv_settings`). */
  settings: (clubId: string | null | undefined) => tuple(root, "settings", clubId),
  /** Relatório de day use por período (painel + export nominal). */
  dayUse: (
    clubId: string | null | undefined,
    viewScope: unknown,
    startDay: string,
    endDay: string,
  ) => tuple(root, "dayUse", clubId, viewScope, startDay, endDay),
} as const;
